/* eslint-disable func-style */
import { ErrorMessage } from '@/messages/error';
import { currentYear, formatUserDate, isValidDate } from '@/utils/date';
import { zxcvbnAsync } from '@zxcvbn-ts/core';

export const required = (value: any): string | true =>
    (Array.isArray(value) ? value.length > 0 : typeof value === 'string' ? value.trim().length > 0 : value != null) || 'Is verplicht';

export const spaceless = (value: any): string | true => !(value || '').includes(' ') || 'Geen spaties toegestaan';

export const numerical = (value: any): string | true => value == null || Number.isInteger(Number(value)) || 'Moet numeriek zijn';

export const positive = (value: number | null): string | true => value == null || value > 0 || 'Moet hoger zijn dan 0';

export function email(value: string | null): string | true {
    return value == null || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) || value === '' || ErrorMessage.EMAIL_INVALID;
}

export const phone = (value: string | null): string | true =>
    value == null || /(?=.{0,16}$)^\d(?:\s?\d|\s?-)*$/.test(value) || value === '' || 'Geen geldig telefoonnummer ingevuld';

export const postalCode = (value: string | null): string | true =>
    value == null || /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i.test(value) || 'Geen geldige postcode ingevuld';

export const noSpecialChars = (value: string | null): string | true =>
    value == null || /^[ \p{L}\p{N}\p{M}-]+$/u.test(value) || value === '' || 'Mag geen speciale characters bevatten';

export const minLength =
    (minLength: number) =>
    (value: string | any[] | undefined): string | true =>
        value == undefined || value.length >= minLength || `Moet minimaal ${minLength} tekens lang zijn`;

export const maxLength =
    (maxLength: number) =>
    (value: string | any[] | undefined): string | true =>
        value == undefined || value.length <= maxLength || `Mag maximaal ${maxLength} tekens lang zijn`;

export const password = async (value: string): Promise<string | true> => {
    const result = value ? await zxcvbnAsync(value) : { score: 0 };
    return result.score < 3
        ? 'Wachtwoord is te zwak. Zorg voor minimaal 8 tekens, gebruik een mix van hoofdletters, kleine letters, cijfers en speciale tekens.'
        : true;
};

export const confirmPassword =
    (password: string) =>
    (value: any): string | true =>
        value === password || 'Wachtwoorden komen niet overeen';

export const isValidDateFormat = (date?: string): string | true => {
    if (!date) return true;
    return !isValidDate(date) ? 'Geen geldige datum' : true;
};

export const isValidYearRange = (year: number): string | true => {
    const maxYear = currentYear + 10;
    const minYear = currentYear - 10;

    if (year > maxYear) return `Een jaar na ${maxYear} is niet mogelijk`;
    if (year < minYear) return `Een jaar voor ${minYear} is niet mogelijk`;
    return true;
};

export const dateRange =
    (minDate?: string, maxDate?: string) =>
    (value: string | undefined): string | boolean => {
        if (!value) return true;

        const selectedDate = new Date(value);
        if (isNaN(selectedDate.getTime())) return 'Geen geldige datum';

        const min = minDate ? new Date(minDate) : null;
        const max = maxDate ? new Date(maxDate) : null;

        if (min && selectedDate < min) return `Datum moet na ${formatUserDate(min)} zijn`;
        if (max && selectedDate > max) return `Datum moet voor ${formatUserDate(max)} zijn`;

        return true;
    };
